import React, { useContext } from 'react';

import { GridCard } from './grid-card';
import { GridCardContent } from './grid-card-content';
import { GridCardHeader } from './grid-card-header';
import { GridCardOfferFooter } from './grid-card-offer-footer';
import { GridCardProductFooter } from './grid-card-product-footer';

import { ViewportContext } from 'src/components/viewport';
import { useNewKwlpCtaExperiment } from 'src/hooks/experiments';
import { BaseProps, Store } from '../../types';

export const ProductGridCard: React.FC<BaseProps & {
  smallerProductCardsMobileExperiment?: boolean;
}> = props => {
  const {
    brand,
    category,
    diffPercentage,
    highlightedName,
    imageSize,
    imageSrc,
    name,
    previousPriceText,
    price,
    properties,
    rank,
    rating,
    reviewsCount,
    selectable,
    selected,
    shouldLazyLoadImage,
    store,
    type = 'product',
    link,
    onClick,
    className,
    container,
    background,
    onDeselect,
    onSelect,
    buttonAriaLabel,
    ActionArea,
    FooterArea,
    onRankInCategoryClick,
    isKWLP,
  } = props;

  const cardProps = {
    link,
    className,
    container,
    background,
    selected,
    selectable,
    onClick,
    onDeselect,
    onSelect,
    buttonAriaLabel,
    ActionArea,
    FooterArea,
  };

  const headerProps = {
    brand,
    diffPercentage,
    imageSize,
    imageSrc,
    name,
    selectable,
    selected,
    shouldLazyLoadImage,
  };

  const useNewKwlpCta = useNewKwlpCtaExperiment() && isKWLP;

  const contentProps = {
    category,
    diffPercentage,
    highlightedName,
    name,
    price,
    properties,
    rank,
    rating,
    reviewsCount,
    // Don't show shop logo in content for offers. Only pass currency for offers. (We also need the full store information for the new-kwlp-cta experiment)
    store:
      type === 'product' || useNewKwlpCta
        ? store
        : ({
            currency: store?.currency,
          } as Store),
  };

  const footerProps = {
    type,
    rank,
    previousPriceText,
    category,
    store,
    link,
    onClick,
    onRankInCategoryClick,
    useNewKwlpCta,
  };

  // The card shouldn't be clickable for offers
  const isClickable = type === 'product';

  const { isMobile } = useContext(ViewportContext);

  return (
    <GridCard
      {...cardProps}
      isClickable={isClickable}
      FooterArea={FooterArea || <Footer {...footerProps} />}
      ActionArea={ActionArea}
      outlined={!props.smallerProductCardsMobileExperiment || !isMobile}
    >
      <GridCardHeader {...headerProps} />
      <GridCardContent
        {...contentProps}
        useNewKwlpCta={useNewKwlpCta}
        smallerProductCardsMobileExperiment={
          props.smallerProductCardsMobileExperiment
        }
      />
    </GridCard>
  );
};

type FooterProps = Pick<
  BaseProps,
  | 'type'
  | 'rank'
  | 'previousPriceText'
  | 'category'
  | 'store'
  | 'link'
  | 'onClick'
  | 'onRankInCategoryClick'
> & {
  useNewKwlpCta?: boolean;
};

const Footer: React.FC<FooterProps> = props => {
  const {
    type,
    rank,
    previousPriceText,
    category,
    store,
    link,
    onClick,
    onRankInCategoryClick,
    useNewKwlpCta,
  } = props;

  const productFooterProps = {
    rank,
    previousPriceText,
    category,
    store,
    link,
    onClick,
    onRankInCategoryClick,
  };
  const offerFooterProps = { store, link, onClick };

  if (type === 'product') {
    return (
      <GridCardProductFooter
        useNewKwlpCta={useNewKwlpCta}
        {...productFooterProps}
      />
    );
  }

  if (type === 'offer') {
    return (
      <GridCardOfferFooter
        useNewKwlpCta={useNewKwlpCta}
        {...offerFooterProps}
      />
    );
  }

  return null;
};
